import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Users, Eye, Sparkles, Plus, ScrollText, Download, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadCsv, getList } from "@/lib/api";
import Modal, { inputCls, btnPrimary, btnSecondary, Field } from "@/components/Modal";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { ALL_ROLES, ASSIGNABLE_ROLES, AI_TONES, ROLE_LABELS, PAGE_SIZE, fmtDate, fmtDateTime, label } from "@/lib/constants";

function CreateUserForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", role: "rnd_scientist" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/register", form);
      toast.success("User created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Create User" onClose={onClose} testid="create-user-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="create-user-form">
        <Field label="Full name">
          <input data-testid="create-user-name-input" required value={form.full_name} onChange={set("full_name")} className={inputCls} />
        </Field>
        <Field label="Email">
          <input data-testid="create-user-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="person@smoor.com" />
        </Field>
        <Field label="Temporary password">
          <input data-testid="create-user-password-input" type="text" required value={form.password} onChange={set("password")} className={inputCls} placeholder="Min. 8 chars, 1 letter + 1 number" />
        </Field>
        <Field label="Role">
          <select data-testid="create-user-role-select" value={form.role} onChange={set("role")} className={inputCls}>
            {ASSIGNABLE_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </Field>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary} data-testid="create-user-cancel-button">Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary} data-testid="create-user-save-button">
            {saving ? "Creating…" : "Create user"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AiConfigCard({ isAdmin }) {
  const queryClient = useQueryClient();
  const { data: config } = useQuery({
    queryKey: ["ai-config"],
    queryFn: async () => (await api.get("/ai/config")).data,
  });

  const save = async (patch) => {
    try {
      await api.put("/ai/config", { enabled: config.enabled, tone: config.tone, ...patch });
      toast.success("AI configuration saved");
      queryClient.invalidateQueries({ queryKey: ["ai-config"] });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="ai-config-card">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={17} className="text-[#C5A059]" />
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">AI Copilot Configuration</h2>
      </div>
      {!config ? (
        <p className="text-sm text-[#8C7A6B]">Loading…</p>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-[#2A1810]">AI features</div>
              <div className="text-xs text-[#8C7A6B]">Sensory summaries and formula advisor</div>
            </div>
            <button
              disabled={!isAdmin}
              onClick={() => save({ enabled: !config.enabled })}
              data-testid="ai-enabled-toggle"
              className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-50 ${config.enabled ? "bg-[#2A1810]" : "bg-[#D8CBB8]"}`}
            >
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${config.enabled ? "left-6" : "left-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-[#2A1810]">Tone</div>
              <div className="text-xs text-[#8C7A6B]">How verbose generated analysis is</div>
            </div>
            <select
              disabled={!isAdmin}
              value={config.tone}
              onChange={(e) => save({ tone: e.target.value })}
              className={`${inputCls} !w-36 !py-1.5 disabled:opacity-50`}
              data-testid="ai-tone-select"
            >
              {AI_TONES.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-[#F4EFEA]">
            <span className="text-[#8C7A6B]">Generations used</span>
            <span className="font-bold text-[#2A1810]" data-testid="ai-usage-count">{config.usage_count ?? 0}</span>
          </div>
          {!isAdmin && (
            <p className="text-xs text-[#8C7A6B]" data-testid="ai-config-readonly">Only admins can change AI settings.</p>
          )}
        </div>
      )}
    </div>
  );
}

function AlertThresholdsCard({ isAdmin }) {
  const queryClient = useQueryClient();
  const { data: config } = useQuery({
    queryKey: ["alert-config"],
    queryFn: async () => (await api.get("/config/alerts")).data,
  });
  const [draft, setDraft] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const values = draft ?? config;

  const save = async () => {
    try {
      await api.put("/config/alerts", {
        drift_threshold: Number(values.drift_threshold),
        cost_rise_percent: Number(values.cost_rise_percent),
      });
      toast.success("Alert thresholds saved");
      setDraft(null);
      queryClient.invalidateQueries({ queryKey: ["alert-config"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="alert-config-card">
      <div className="flex items-center gap-2 mb-4">
        <BellRing size={17} className="text-[#C5A059]" />
        <h2 className="font-display text-lg font-semibold text-[#1C100B]">Alert Thresholds</h2>
      </div>
      {!values ? (
        <p className="text-sm text-[#8C7A6B]">Loading…</p>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-[#2A1810]">Shelf-life drift</div>
              <div className="text-xs text-[#8C7A6B]">Quality points lost between two checkpoints</div>
            </div>
            <input
              type="number" step="0.5" min={0.1} max={10}
              disabled={!isAdmin}
              value={values.drift_threshold}
              onChange={(e) => setDraft({ ...values, drift_threshold: e.target.value })}
              className={`${inputCls} !w-24 !py-1.5 text-right disabled:opacity-50`}
              data-testid="drift-threshold-input"
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-[#2A1810]">Cost rise</div>
              <div className="text-xs text-[#8C7A6B]">% increase since the last approved version</div>
            </div>
            <input
              type="number" step="0.5" min={0.1} max={100}
              disabled={!isAdmin}
              value={values.cost_rise_percent}
              onChange={(e) => setDraft({ ...values, cost_rise_percent: e.target.value })}
              className={`${inputCls} !w-24 !py-1.5 text-right disabled:opacity-50`}
              data-testid="cost-rise-input"
            />
          </div>
          {isAdmin ? (
            <>
              <button onClick={save} disabled={!draft} className={`${btnPrimary} w-full justify-center disabled:opacity-50`} data-testid="save-thresholds-button">
                Save thresholds
              </button>
              <div className="pt-3 border-t border-[#F4EFEA]">
                <button
                  onClick={async () => {
                    setBackfilling(true);
                    try {
                      const res = await api.post("/config/backfill-cost-baselines");
                      toast.success(res.data.updated
                        ? `Cost baseline recorded on ${res.data.updated} approved version${res.data.updated > 1 ? "s" : ""}`
                        : "All approved versions already have a cost baseline");
                      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                    } catch (err) {
                      toast.error(apiError(err));
                    } finally {
                      setBackfilling(false);
                    }
                  }}
                  disabled={backfilling}
                  className={`${btnSecondary} w-full justify-center disabled:opacity-50`}
                  data-testid="backfill-baselines-button"
                >
                  {backfilling ? "Backfilling…" : "Backfill cost baselines"}
                </button>
                <p className="text-xs text-[#8C7A6B] mt-2">
                  Records the cost on older approved versions so Cost Watch can spot historic creep. Safe to run more than once.
                </p>
              </div>
            </>
          ) : (
            <p className="text-xs text-[#8C7A6B]" data-testid="alert-config-readonly">Only admins can change alert thresholds.</p>
          )}
        </div>
      )}
    </div>
  );
}

function AuditCard({ users }) {
  const [filters, setFilters] = useState({ entity_type: "", user_id: "", date_from: "", date_to: "" });
  const [offset, setOffset] = useState(0);

  const { data: entityTypes = [] } = useQuery({
    queryKey: ["audit-entity-types"],
    queryFn: async () => (await api.get("/audit/entity-types")).data,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["audit", filters, offset],
    queryFn: () => getList("/audit", { ...filters, limit: PAGE_SIZE, offset }),
  });

  const entries = data?.items ?? [];
  const setFilter = (k) => (e) => { setOffset(0); setFilters({ ...filters, [k]: e.target.value }); };

  const exportCsv = async () => {
    try {
      await downloadCsv("/audit/export.csv", "smoor-audit-log.csv", filters);
      toast.success("Audit log exported");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden mt-6" data-testid="audit-card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 px-6 py-4 border-b border-[#F4EFEA]">
        <div className="flex items-center gap-2">
          <ScrollText size={17} className="text-[#C5A059]" />
          <h2 className="font-display text-lg font-semibold text-[#1C100B]">Audit Trail</h2>
        </div>
        <button onClick={exportCsv} className={btnSecondary} data-testid="export-audit-button">
          <Download size={15} className="inline mr-1.5 -mt-0.5" /> Export CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3 px-6 py-4 border-b border-[#F4EFEA]" data-testid="audit-filters">
        <select data-testid="audit-entity-filter" value={filters.entity_type} onChange={setFilter("entity_type")} className={`${inputCls} w-48`}>
          <option value="">All entities</option>
          {entityTypes.map((t) => <option key={t} value={t}>{label(t)}</option>)}
        </select>
        <select data-testid="audit-user-filter" value={filters.user_id} onChange={setFilter("user_id")} className={`${inputCls} w-56`}>
          <option value="">All users</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <input data-testid="audit-date-from" type="date" value={filters.date_from} onChange={setFilter("date_from")} className={`${inputCls} w-40`} />
        <input data-testid="audit-date-to" type="date" value={filters.date_to} onChange={setFilter("date_to")} className={`${inputCls} w-40`} />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="audit-table">
          <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 font-semibold">When</th>
              <th className="px-4 py-3 font-semibold">User</th>
              <th className="px-4 py-3 font-semibold">Entity</th>
              <th className="px-4 py-3 font-semibold">Action</th>
              <th className="px-4 py-3 font-semibold">Summary</th>
              <th className="px-4 py-3 font-semibold">Fields</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]">Loading…</td></tr>}
            {!isLoading && entries.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#8C7A6B]" data-testid="audit-empty">No audit entries match these filters.</td></tr>
            )}
            {entries.map((a) => (
              <tr key={a.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`audit-row-${a.id}`}>
                <td className="px-4 py-3 text-[#8C7A6B] whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                <td className="px-4 py-3 text-[#2A1810] font-medium">{a.user_name || "—"}</td>
                <td className="px-4 py-3 text-[#5C4A3E]">{label(a.entity_type)}<div className="text-xs text-[#8C7A6B] truncate max-w-[160px]">{a.entity_name}</div></td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#F4EFEA] text-[#6B584B] border border-[#D8CBB8] px-1.5 py-0.5 rounded">{a.action}</span>
                </td>
                <td className="px-4 py-3 text-[#5C4A3E] max-w-sm"><span className="line-clamp-2">{a.summary || "—"}</span></td>
                <td className="px-4 py-3 text-xs text-[#8C7A6B]">{(a.changed_fields || []).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager offset={offset} limit={PAGE_SIZE} total={data?.total ?? 0} onChange={setOffset} testid="audit-pager" />
    </div>
  );
}

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: isAdmin,
  });

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      toast.success("Role updated");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-8 pb-4 border-b border-[#E8DFD1]">
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#1C100B]">Settings & Admin</h1>
        <p className="text-sm text-[#8C7A6B] mt-1">Account, roles and workspace configuration.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#E8DFD1] rounded-xl p-6 shadow-sm" data-testid="current-user-card">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={17} className="text-[#C5A059]" />
            <h2 className="font-display text-lg font-semibold text-[#1C100B]">Your Account</h2>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#8C7A6B]">Name</span><span className="font-medium text-[#2A1810]" data-testid="settings-user-name">{user?.full_name}</span></div>
            <div className="flex justify-between"><span className="text-[#8C7A6B]">Email</span><span className="font-medium text-[#2A1810]" data-testid="settings-user-email">{user?.email}</span></div>
            <div className="flex justify-between items-center">
              <span className="text-[#8C7A6B]">Role</span>
              <span className="bg-[#F9F3E6] text-[#8A6D2F] border border-[#E5C992] text-xs font-bold px-2.5 py-1 rounded-full" data-testid="settings-user-role">
                {ROLE_LABELS[user?.role] || user?.role}
              </span>
            </div>
            {user?.role === "leadership" && (
              <div className="flex items-start gap-2 mt-2 bg-[#F9F3E6] border border-[#E5C992] rounded-lg px-3 py-2.5 text-xs text-[#8A6D2F]" data-testid="settings-readonly-note">
                <Eye size={14} className="shrink-0 mt-0.5" />
                Leadership accounts have read-only access. Editing is disabled across all modules.
              </div>
            )}
          </div>
        </div>

        <AiConfigCard isAdmin={isAdmin} />
        <AlertThresholdsCard isAdmin={isAdmin} />
      </div>

      <div className="bg-white border border-[#E8DFD1] rounded-xl shadow-sm overflow-hidden" data-testid="user-management-card">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[#F4EFEA]">
          <div className="flex items-center gap-2">
            <Users size={17} className="text-[#C5A059]" />
            <h2 className="font-display text-lg font-semibold text-[#1C100B]">User Management</h2>
            {!isAdmin && <span className="text-xs text-[#8C7A6B] ml-2">(visible to admins only)</span>}
          </div>
          {isAdmin && (
            <button onClick={() => setShowCreate(true)} className={btnPrimary} data-testid="create-user-button">
              <Plus size={15} /> Create user
            </button>
          )}
        </div>
        {isAdmin ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-[#FAF6F0] text-[#2A1810] text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Email</th>
                  <th className="px-4 py-3 text-left font-semibold">Role</th>
                  <th className="px-4 py-3 text-left font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#F4EFEA] last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-[#2A1810]">{u.full_name}</td>
                    <td className="px-4 py-3 text-[#5C4A3E]">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.id === user.id ? (
                        <span className="text-xs font-bold text-[#8A6D2F]">{ROLE_LABELS[u.role]} (you)</span>
                      ) : (
                        <select
                          value={u.role}
                          onChange={(e) => changeRole(u.id, e.target.value)}
                          className={`${inputCls} !w-52 !py-1.5`}
                          data-testid={`role-select-${u.id}`}
                        >
                          {ALL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#8C7A6B]">{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-8 text-sm text-[#8C7A6B]" data-testid="users-table-restricted">
            You need admin access to manage users. Contact the workspace owner.
          </div>
        )}
      </div>

      {isAdmin && <AuditCard users={users} />}

      {showCreate && (
        <CreateUserForm
          onClose={() => setShowCreate(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ["users"] })}
        />
      )}
    </div>
  );
}
